import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart } from "lucide-react";

/**
 * Envelope - interactive envelope that opens on click.
 * Once open, an elegant letter card scales into view with the
 * love letter content (children).
 */
export const Envelope = ({ children, greeting = "My Dearest Lakshita," }) => {
    const [open, setOpen] = useState(false);

    return (
        <div className="relative w-full flex flex-col items-center">
            {!open && (
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.8 }}
                    className="font-hand text-2xl text-dusty-rose-deep mb-6"
                >
                    tap the envelope, my love ↓
                </motion.p>
            )}

            <div className="envelope-perspective relative w-[320px] sm:w-[420px] h-[240px] sm:h-[300px]">
                {/* Envelope body */}
                <motion.button
                    type="button"
                    onClick={() => setOpen(true)}
                    disabled={open}
                    data-testid="envelope-open-button"
                    aria-label="Open love letter"
                    className="group absolute inset-0 rounded-md overflow-hidden focus:outline-none"
                    whileHover={{ scale: open ? 1 : 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    {/* Back of envelope */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blush to-dusty" />
                    {/* Envelope pocket base (bottom triangle detail) */}
                    <div
                        className="absolute inset-x-0 bottom-0 h-3/4 bg-[#F7C6CB]"
                        style={{
                            clipPath:
                                "polygon(0 40%, 50% 100%, 100% 40%, 100% 100%, 0 100%)",
                        }}
                    />
                    {/* Side folds */}
                    <div
                        className="absolute inset-y-0 left-0 w-1/2 bg-[#FBB8BF]/70"
                        style={{ clipPath: "polygon(0 0, 100% 50%, 0 100%)" }}
                    />
                    <div
                        className="absolute inset-y-0 right-0 w-1/2 bg-[#FBB8BF]/70"
                        style={{ clipPath: "polygon(100% 0, 0 50%, 100% 100%)" }}
                    />

                    {/* Wax seal */}
                    {!open && (
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
                            <div className="relative w-16 h-16 rounded-full bg-dusty-rose flex items-center justify-center shadow-lg ring-4 ring-white/30 group-hover:rotate-12 transition-transform duration-500">
                                <Heart
                                    className="w-7 h-7 text-white"
                                    fill="white"
                                />
                            </div>
                        </div>
                    )}

                    {/* Flap */}
                    <div
                        className={`flap absolute inset-x-0 top-0 h-3/5 z-20 ${open ? "open" : ""}`}
                        style={{
                            clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                            background:
                                "linear-gradient(180deg, #FDB8C0 0%, #F7A5AD 100%)",
                        }}
                    />

                    {/* Subtle stamp */}
                    <div className="absolute top-3 right-3 w-14 h-16 rounded-sm bg-white/70 border border-dusty flex items-center justify-center rotate-6 z-10">
                        <span className="font-hand text-dusty-rose-deep text-xl leading-none text-center">
                            S
                            <br />
                            <span className="text-sm">+</span>
                            <br />
                            L
                        </span>
                    </div>
                </motion.button>
            </div>

            {/* Letter */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{
                            delay: 0.5,
                            duration: 0.9,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="relative mt-10 w-full max-w-2xl bg-ivory letter-shadow rounded-md px-8 sm:px-14 py-10 sm:py-14 border border-dusty/60"
                        data-testid="love-letter-content"
                    >
                        {/* Corner floral doodle */}
                        <div className="absolute -top-6 -left-6 w-16 h-16 rounded-full bg-blush/50 blur-md" />
                        <div className="absolute -bottom-4 -right-4 w-20 h-20 rounded-full bg-peach/50 blur-md" />

                        <p className="font-hand text-3xl sm:text-4xl text-dusty-rose-deep">
                            {greeting}
                        </p>
                        <div className="mt-6 font-serif text-cocoa text-lg sm:text-xl leading-[1.85] italic">
                            {children}
                        </div>
                        <div className="mt-10 flex items-end justify-between">
                            <p className="font-serif text-cocoa-soft text-sm">
                                — sealed with everything I am
                            </p>
                            <div className="text-right">
                                <p className="font-hand text-3xl text-dusty-rose-deep leading-tight">
                                    Forever yours ♡
                                </p>
                                <p className="font-hand text-2xl text-cocoa-soft mt-1">
                                    — Shaurya
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Envelope;
